import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, MapPin, QrCode, ClipboardList, Users, LogOut, Menu, X, TrendingUp, AlertTriangle, Wrench } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", exact: true },
  { to: "/compliance", icon: TrendingUp, label: "Cumplimiento", testid: "nav-compliance" },
  { to: "/incidents", icon: AlertTriangle, label: "Incidencias", testid: "nav-incidents" },
  { to: "/clients", icon: Building2, label: "Clientes", testid: "nav-clients", hideForClient: true },
  { to: "/locations", icon: MapPin, label: "Sucursales", testid: "nav-locations" },
  { to: "/areas", icon: QrCode, label: "Áreas / QR", testid: "nav-areas" },
  { to: "/history", icon: ClipboardList, label: "Historial", testid: "nav-history" },
  { to: "/users", icon: Users, label: "Usuarios", testid: "nav-users", adminOnly: true },
  { to: "/tools", icon: Wrench, label: "Herramientas", testid: "nav-tools", adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const items = NAV.filter(n => {
    if (n.adminOnly && user?.role !== "admin") return false;
    if (n.hideForClient && user?.role === "cliente") return false;
    return true;
  });

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-lx-border bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-lx-border">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={n.testid}
              className={({ isActive }) => `lx-sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon size={18} strokeWidth={1.8} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-lx-border">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-lx-muted truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1 font-bold text-lx-primary">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="lx-btn lx-btn-ghost w-full justify-start"
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-lx-border h-14 flex items-center justify-between px-4">
        <Logo size={22} />
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-lx-surface">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-14">
          <nav className="px-4 py-4 space-y-1">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testid}`}
                className={({ isActive }) => `lx-sidebar-link ${isActive ? "active" : ""}`}
              >
                <n.icon size={20} />
                <span>{n.label}</span>
              </NavLink>
            ))}
            <button onClick={handleLogout} data-testid="mobile-logout" className="lx-sidebar-link w-full text-left">
              <LogOut size={20} /> Cerrar sesión
            </button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
